import {
  Color,
  DoubleSide,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Vector3,
  type PerspectiveCamera,
} from 'three';
import { RENDER } from '../config';

/**
 * Vrstva mraků.
 *
 * Jedna vodorovná plocha vysoko nad světem s procedurálním fbm šumem.
 * Není to volumetrika — ale s falešným samostíněním (druhý vzorek šumu
 * posunutý směrem ke slunci) to na dálku čte jako objem a stojí to
 * jeden draw call a pár instrukcí na pixel.
 *
 * Plocha musí doblednout dřív, než dosáhne far plane kamery, jinak by byl
 * vidět ostře useknutý okraj geometrie.
 */
export class Clouds {
  readonly mesh: Mesh;
  private readonly uniforms;

  constructor() {
    const size = 9000;
    const geometry = new PlaneGeometry(size, size, 1, 1);
    geometry.rotateX(-Math.PI / 2);

    this.uniforms = {
      uTime: { value: 0 },
      uSunDirection: { value: new Vector3(0, -1, 0) },
      uLit: { value: new Color(1.35, 1.32, 1.28) },
      uShadow: { value: new Color(0.42, 0.47, 0.57) },
      /** Nižší hodnota = víc mraků. */
      uCoverage: { value: 0.33 },
      uOpacity: { value: 0.95 },
      uFadeStart: { value: 1500 },
      uFadeEnd: { value: RENDER.far * 0.88 },
    };

    const material = new ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      side: DoubleSide,
      vertexShader: /* glsl */ `
        varying vec3 vWorldPos;
        void main() {
          vec4 world = modelMatrix * vec4(position, 1.0);
          vWorldPos = world.xyz;
          gl_Position = projectionMatrix * viewMatrix * world;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uSunDirection;
        uniform vec3 uLit;
        uniform vec3 uShadow;
        uniform float uCoverage;
        uniform float uOpacity;
        uniform float uFadeStart;
        uniform float uFadeEnd;
        varying vec3 vWorldPos;

        float cHash(vec2 p) {
          vec3 p3 = fract(vec3(p.xyx) * 0.1031);
          p3 += dot(p3, p3.yzx + 33.33);
          return fract((p3.x + p3.y) * p3.z);
        }
        float cNoise(vec2 p) {
          vec2 i = floor(p); vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(cHash(i), cHash(i + vec2(1.0, 0.0)), u.x),
                     mix(cHash(i + vec2(0.0, 1.0)), cHash(i + vec2(1.0, 1.0)), u.x), u.y);
        }
        float cFbm(vec2 p) {
          float s = 0.0, a = 0.5;
          for (int i = 0; i < 6; i++) { s += a * cNoise(p); p *= 2.13; a *= 0.5; }
          return s;
        }
        // Domain warp rozbíjí pravidelnost oktáv — bez něj mraky čtou jako šmouhy.
        float cClouds(vec2 p) {
          vec2 w = vec2(cFbm(p * 0.6 + 11.3), cFbm(p * 0.6 + 27.9)) - 0.5;
          return cFbm(p + w * 1.15);
        }

        void main() {
          vec2 drift = vec2(uTime * 0.0016, uTime * 0.0009);
          vec2 p = vWorldPos.xz * 0.00042 + drift;

          float density = cClouds(p);
          float cover = smoothstep(uCoverage, uCoverage + 0.20, density);
          if (cover <= 0.001) discard;

          // Falešné samostínění: vzorek posunutý proti směru slunce.
          // Kde je "nad" fragmentem víc hmoty, tam je mrak tmavší.
          vec2 lightOffset = normalize(uSunDirection.xz + vec2(1e-4)) * 0.055;
          float above = cClouds(p - lightOffset);
          float lit = smoothstep(-0.16, 0.14, density - above);

          vec3 color = mix(uShadow, uLit, lit);

          // Okraje chomáčů jsou prosvícené, střed hutný.
          float rim = 1.0 - smoothstep(0.0, 0.55, cover);
          color = mix(color, uLit, rim * 0.35);

          float dist = length(vWorldPos - cameraPosition);
          float fade = 1.0 - smoothstep(uFadeStart, uFadeEnd, dist);

          gl_FragColor = vec4(color, cover * fade * uOpacity);
        }
      `,
    });

    this.mesh = new Mesh(geometry, material);
    this.mesh.position.y = 1350;
    this.mesh.name = 'clouds';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;
  }

  update(elapsed: number, camera: PerspectiveCamera, sunDirection: Vector3): void {
    this.uniforms.uTime.value = elapsed;
    this.uniforms.uSunDirection.value.copy(sunDirection);
    // Vrstva jede s kamerou, takže hráč nikdy nedojde na její okraj.
    this.mesh.position.x = camera.position.x;
    this.mesh.position.z = camera.position.z;
  }

  set coverage(value: number) {
    this.uniforms.uCoverage.value = value;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as ShaderMaterial).dispose();
  }
}
